import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type RFB from '@novnc/novnc';
import type { RfbClipboardEvent } from '@novnc/novnc';

import { copiedText, COPY_WAIT_MS, fromMachine, KEYSYM, letterKeysym, shortcutAction, toMachine } from '../lib/mission/screenKeys';

/** The computer is a Mac (Cmd is the shortcut key there). */
function onMac(): boolean {
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/**
 * Copy and paste between the computer and the workshop's screen, with nothing to set up (PLAN-ATELIER-OTTO
 * § 17.6): the viewer's keyboard shortcuts are looked at before noVNC (`shortcutAction`). Paste lets the
 * browser paste into a hidden field — no clipboard permission is asked — and hands the text to the
 * machine before typing Ctrl+V there. Copy types Ctrl+C there and writes the machine's answer on the
 * computer; a browser that refuses (Safari outside a click) leaves the text in `copyFallback` for a
 * one-click copy. Only while the user may act: a viewer in read-only mode sends and receives nothing.
 */
export function useScreenClipboard(frame: RefObject<HTMLElement | null>, rfb: RFB | null, enabled: boolean) {
  const [copyFallback, setCopyFallback] = useState<string | null>(null);
  const last = useRef<{ text: string; at: number } | null>(null);
  const copyAskedAt = useRef<number | null>(null);

  const deliver = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFallback(null);
    } catch {
      setCopyFallback(text);
    }
  }, []);

  useEffect(() => {
    const root = frame.current;
    if (!root || !rfb || !enabled) return undefined;
    const mac = onMac();

    const typeCtrl = (letter: string, shift: boolean) => {
      rfb.sendKey(KEYSYM.controlL, 'ControlLeft', true);
      if (shift) rfb.sendKey(KEYSYM.shiftL, 'ShiftLeft', true);
      rfb.sendKey(letterKeysym(letter, shift), `Key${letter.toUpperCase()}`);
      if (shift) rfb.sendKey(KEYSYM.shiftL, 'ShiftLeft', false);
      rfb.sendKey(KEYSYM.controlL, 'ControlLeft', false);
    };

    const askCopy = () => {
      const askedAt = Date.now();
      copyAskedAt.current = askedAt;
      window.setTimeout(() => {
        if (copyAskedAt.current !== askedAt) return;   // the machine answered in time
        copyAskedAt.current = null;
        const text = copiedText({ requestedAt: askedAt, now: Date.now(), last: last.current });
        if (text !== null) void deliver(text);
      }, COPY_WAIT_MS);
    };

    // The browser's own paste lands here: its text is read without asking for the clipboard.
    const sink = document.createElement('textarea');
    sink.setAttribute('aria-hidden', 'true');
    sink.tabIndex = -1;
    sink.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
    root.appendChild(sink);

    const onPaste = (event: ClipboardEvent) => {
      event.preventDefault();
      const text = event.clipboardData?.getData('text/plain') ?? '';
      rfb.focus();
      if (!text) return;
      rfb.clipboardPasteFrom(toMachine(text));
      typeCtrl('v', false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const action = shortcutAction(event, mac);
      if (!action) return;
      if (action.kind === 'copy' && action.native) {
        askCopy();   // noVNC types Ctrl+C itself
        return;
      }
      event.stopPropagation();   // noVNC never sees it
      if (action.kind === 'local') return;   // the computer's own shortcut: its default action stays
      if (action.kind === 'paste') {
        sink.value = '';
        sink.focus();   // the default action, not prevented, pastes into the sink
        window.setTimeout(() => {
          if (document.activeElement === sink) rfb.focus();
        }, 400);
        return;
      }
      event.preventDefault();
      if (action.kind === 'copy') {
        typeCtrl(action.letter, false);
        askCopy();
        return;
      }
      typeCtrl(action.letter, action.shift);
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const action = shortcutAction(event, mac);
      if (action && !(action.kind === 'copy' && action.native)) event.stopPropagation();
    };

    const onClipboard = (event: Event) => {
      const text = fromMachine((event as RfbClipboardEvent).detail.text);
      last.current = { text, at: Date.now() };
      if (copyAskedAt.current === null) return;   // a selection on the machine: nothing leaves without Cmd+C
      copyAskedAt.current = null;
      void deliver(text);
    };

    sink.addEventListener('paste', onPaste);
    root.addEventListener('keydown', onKeyDown, true);
    root.addEventListener('keyup', onKeyUp, true);
    rfb.addEventListener('clipboard', onClipboard);
    return () => {
      sink.removeEventListener('paste', onPaste);
      root.removeEventListener('keydown', onKeyDown, true);
      root.removeEventListener('keyup', onKeyUp, true);
      rfb.removeEventListener('clipboard', onClipboard);
      sink.remove();
    };
  }, [frame, rfb, enabled, deliver]);

  const copyNow = useCallback(() => {
    if (copyFallback !== null) void deliver(copyFallback);
  }, [copyFallback, deliver]);

  return {
    copyFallback,
    copyNow,
    dismissCopy: () => setCopyFallback(null),
    /** The shortcuts as this computer writes them. */
    keys: onMac() ? '⌘C / ⌘V' : 'Ctrl+C / Ctrl+V',
  };
}
